import { useEffect, useRef, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { api } from "../api";
import { Input } from "./ui/input";

/**
 * Google Places (New) autocomplete input — proxies via /api/places so the
 * API key never ships to the browser. Degrades to a plain input when the
 * key is missing, the key lacks "Places API (New)" enablement, or any
 * network/Google error — the field is ALWAYS fully editable afterwards.
 *
 * Props:
 *   value           : current street value (string)
 *   onChange(v)     : fired for every keystroke (plain-input semantics)
 *   onPicked(addr)  : fired once when a suggestion is chosen; addr has
 *                     { street, city, postal_code, country_code, country_name }
 *   country         : optional ISO-3166-1 alpha-2 bias
 *   testid          : base data-testid for the input + dropdown
 *   placeholder     : passthrough
 *   disabled, name, className — passthroughs
 */
export default function AddressAutocomplete({
  value, onChange, onPicked, country = null, testid = "address-ac",
  placeholder = "Street address", disabled, name, className,
}) {
  const [available, setAvailable] = useState(null); // null = probing
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resolving, setResolving] = useState(false);
  const abortRef = useRef(null);
  const blurTimerRef = useRef(null);

  // One-shot availability probe.
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const { data } = await api.get("/places/available");
        if (!dead) setAvailable(!!data?.available);
      } catch { if (!dead) setAvailable(false); }
    })();
    return () => { dead = true; };
  }, []);

  // Debounced suggestion fetch.
  useEffect(() => {
    if (available === false) return; // graceful plain-input
    if (!value || value.trim().length < 3) { setSuggestions([]); setOpen(false); return; }
    const t = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setBusy(true);
      try {
        const params = new URLSearchParams({ q: value });
        if (country) params.set("country", country);
        const { data } = await api.get(`/places/autocomplete?${params.toString()}`, { signal: ctrl.signal });
        if (!data?.available) {
          setAvailable(false);
          setSuggestions([]);
          return;
        }
        setSuggestions(data.suggestions || []);
        setOpen((data.suggestions || []).length > 0);
      } catch { /* network/abort — silent, keep plain-input */ }
      finally { setBusy(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [value, country, available]);

  const pick = async (sug) => {
    setOpen(false);
    setResolving(true);
    try {
      const { data } = await api.get(`/places/details?place_id=${encodeURIComponent(sug.place_id)}`);
      if (!data?.available) { setAvailable(false); return; }
      // Apply only non-empty fields so we never clobber something the user typed.
      const addr = {
        street: data.street || sug.main_text || value,
        city: data.city || "",
        postal_code: data.postal_code || "",
        country_code: data.country_code || "",
        country_name: data.country_name || "",
      };
      onChange(addr.street);
      onPicked?.(addr);
    } catch { /* silent */ }
    finally { setResolving(false); }
  };

  return (
    <div className="relative">
      <Input
        name={name}
        className={className || "h-9"}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => { if (suggestions.length) setOpen(true); }}
        onBlur={() => { blurTimerRef.current = setTimeout(() => setOpen(false), 150); }}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testid}
        autoComplete="off"
      />
      {(busy || resolving) && (
        <Loader2 size={14} className="animate-spin absolute right-2 top-1/2 -translate-y-1/2 text-[#5C665A]" />
      )}
      {open && suggestions.length > 0 && (
        <div
          className="absolute z-30 mt-1 w-full bg-white border border-[#E2E4E0] rounded-md shadow-md max-h-64 overflow-auto"
          data-testid={`${testid}-dropdown`}
          onMouseDown={(e) => { e.preventDefault(); clearTimeout(blurTimerRef.current); }}
        >
          {suggestions.map((s, i) => (
            <button
              key={s.place_id}
              type="button"
              className="w-full text-left px-3 py-1.5 hover:bg-[#F2F5F0] flex items-start gap-2 border-t border-[#F2F2F0] first:border-t-0"
              onClick={() => pick(s)}
              data-testid={`${testid}-option-${i}`}
            >
              <MapPin size={13} className="text-[#5C665A] mt-0.5 shrink-0" />
              <span className="text-sm leading-snug">
                <span className="font-medium text-[#1A1D1A]">{s.main_text}</span>
                {s.secondary_text && <span className="text-[#5C665A]"> · {s.secondary_text}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
